import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api/client';
import type { BudgetStatus, Category, RecurringExpense } from '../api/types';
import { formatAmount, formatEuro } from '../lib/format';
import { usePeriod } from '../lib/period';
import { MonthSwitcher } from '../components/MonthSwitcher';
import { Bar, Button, Card, Dot, EmptyState, SectionTitle, Spinner } from '../components/ui';

/** Colore neutro della voce "Altro", uguale al default delle categorie. */
const OTHER_COLOR = '#64748b';

const toAmount = (value: string) => Number(value.replace(',', '.')) || 0;

/**
 * Budget del mese e spese fisse. "Copia dal mese precedente" e' quello che
 * nel foglio si faceva duplicando la scheda a mano.
 */
export function BudgetPage() {
  const [period, setPeriod] = usePeriod();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [otherDraft, setOtherDraft] = useState('');
  const [showOtherDetail, setShowOtherDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: status, isLoading } = useQuery({
    queryKey: ['budgets', period.year, period.month],
    queryFn: () => api.get<BudgetStatus>(`/budgets?year=${period.year}&month=${period.month}`),
  });

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<Category[]>('/categories'),
  });

  useEffect(() => {
    if (!status) return;
    setDraft(Object.fromEntries(status.rows.map((r) => [r.categoryId, formatAmount(r.budget)])));
    setOtherDraft(status.other.budget > 0 ? formatAmount(status.other.budget) : '');
  }, [status]);

  const save = useMutation({
    mutationFn: () =>
      api.put<BudgetStatus>('/budgets', {
        year: period.year,
        month: period.month,
        rows: Object.entries(draft)
          .map(([categoryId, value]) => ({
            categoryId,
            amount: Number(value.replace(',', '.')) || 0,
          }))
          .filter((r) => r.amount > 0),
        other: toAmount(otherDraft),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      setEditing(false);
      setError(null);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Salvataggio non riuscito'),
  });

  const copyPrevious = useMutation({
    mutationFn: () => api.post<BudgetStatus>('/budgets/copy-from-previous', period),
    onSuccess: () => queryClient.invalidateQueries(),
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Copia non riuscita'),
  });

  if (isLoading || !status) return <Spinner />;

  const draftTotal =
    Object.values(draft).reduce((sum, v) => sum + toAmount(v), 0) + toAmount(otherDraft);

  const { other } = status;
  const showOther = editing || other.budget > 0 || other.spent > 0;
  // Se esiste una categoria "Altro" vera, la voce di raccolta non va confusa con lei.
  const otherLabel = (categories ?? []).some((c) => c.name.trim().toLowerCase() === 'altro')
    ? 'Altro (non budgettate)'
    : 'Altro';

  return (
    <div>
      <MonthSwitcher period={period} onChange={setPeriod} />

      <div className="flex flex-col gap-5">
        {error && (
          <p
            role="alert"
            className="rounded-[var(--radius-control)] bg-danger-soft px-3 py-2 text-sm text-danger"
          >
            {error}
          </p>
        )}

        <Card>
          <div className="mb-4 flex items-baseline justify-between">
            <div>
              <div className="text-sm text-muted">Budget del mese</div>
              <div className="tnum text-2xl font-semibold">
                {formatEuro(editing ? draftTotal : status.totalBudget)}
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-muted">Speso</div>
              <div
                className={`tnum text-2xl font-semibold ${
                  status.totalSpent > status.totalBudget ? 'text-danger' : ''
                }`}
              >
                {formatEuro(status.totalSpent)}
              </div>
            </div>
          </div>

          {status.rows.length === 0 && !showOther ? (
            <EmptyState
              title="Nessun budget per questo mese"
              hint="Copialo dal mese precedente oppure impostalo a mano."
            />
          ) : (
            <div className="flex flex-col gap-3">
              {(editing ? (categories ?? []).filter((c) => !c.archived) : status.rows).map(
                (row) => {
                  const categoryId = 'categoryId' in row ? row.categoryId : row.id;
                  const statusRow = status.rows.find((r) => r.categoryId === categoryId);
                  return (
                    <div key={categoryId}>
                      <div className="mb-1 flex items-center gap-2">
                        <Dot color={row.color} />
                        <span className="flex-1 truncate text-sm">{row.name}</span>
                        {editing ? (
                          <input
                            inputMode="decimal"
                            value={draft[categoryId] ?? ''}
                            onChange={(e) =>
                              setDraft((d) => ({ ...d, [categoryId]: e.target.value }))
                            }
                            placeholder="0,00"
                            aria-label={`Budget per ${row.name}`}
                            className="tnum w-24 rounded-lg bg-surface-2 px-2 py-1.5 text-right text-sm"
                          />
                        ) : (
                          <span className="tnum text-sm">
                            <span
                              className={
                                statusRow && statusRow.spent > statusRow.budget ? 'text-danger' : ''
                              }
                            >
                              {formatEuro(statusRow?.spent ?? 0)}
                            </span>
                            <span className="text-muted">
                              {' '}
                              / {formatEuro(statusRow?.budget ?? 0)}
                            </span>
                          </span>
                        )}
                      </div>
                      {!editing && statusRow && (
                        <Bar
                          ratio={statusRow.ratio}
                          color={statusRow.ratio > 1 ? 'var(--danger)' : row.color}
                        />
                      )}
                    </div>
                  );
                },
              )}

              {showOther && (
                <div>
                  <div className="mb-1 flex items-center gap-2">
                    <Dot color={OTHER_COLOR} />
                    {editing || other.categories.length === 0 ? (
                      <span className="flex-1 truncate text-sm">{otherLabel}</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setShowOtherDetail((v) => !v)}
                        aria-expanded={showOtherDetail}
                        className="flex-1 truncate text-left text-sm"
                      >
                        {otherLabel} {showOtherDetail ? '▾' : '▸'}
                      </button>
                    )}
                    {editing ? (
                      <input
                        inputMode="decimal"
                        value={otherDraft}
                        onChange={(e) => setOtherDraft(e.target.value)}
                        placeholder="0,00"
                        aria-label={`Budget per ${otherLabel}`}
                        className="tnum w-24 rounded-lg bg-surface-2 px-2 py-1.5 text-right text-sm"
                      />
                    ) : (
                      <span className="tnum text-sm">
                        <span className={other.spent > other.budget ? 'text-danger' : ''}>
                          {formatEuro(other.spent)}
                        </span>
                        <span className="text-muted"> / {formatEuro(other.budget)}</span>
                      </span>
                    )}
                  </div>
                  {!editing && other.budget > 0 && (
                    <Bar
                      ratio={other.ratio}
                      color={other.ratio > 1 ? 'var(--danger)' : OTHER_COLOR}
                    />
                  )}
                  {!editing && showOtherDetail && (
                    <ul className="mt-2 flex flex-col gap-1 pl-5 text-xs text-muted">
                      {other.categories.map((c) => (
                        <li key={c.categoryId} className="flex items-center gap-2">
                          <Dot color={c.color} />
                          <span className="flex-1 truncate">{c.name}</span>
                          <span className="tnum">{formatEuro(c.amount)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="mt-5 flex flex-wrap gap-2">
            {editing ? (
              <>
                <Button onClick={() => save.mutate()} disabled={save.isPending}>
                  {save.isPending ? 'Salvo...' : 'Salva budget'}
                </Button>
                <Button variant="ghost" onClick={() => setEditing(false)}>
                  Annulla
                </Button>
              </>
            ) : (
              <>
                <Button onClick={() => setEditing(true)}>Modifica</Button>
                <Button
                  variant="ghost"
                  onClick={() => copyPrevious.mutate()}
                  disabled={copyPrevious.isPending}
                >
                  Copia dal mese precedente
                </Button>
              </>
            )}
          </div>
        </Card>

        <RecurringSection categories={categories ?? []} />
      </div>
    </div>
  );
}

/** Gestione delle spese fisse: importo, categoria, giorno di proposta. */
function RecurringSection({ categories }: { categories: Category[] }) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ categoryId: '', amount: '', dayOfMonth: '1' });

  const { data: rules } = useQuery({
    queryKey: ['recurring'],
    queryFn: () => api.get<RecurringExpense[]>('/recurring'),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<RecurringExpense>('/recurring', {
        categoryId: form.categoryId,
        amount: Number(form.amount.replace(',', '.')),
        dayOfMonth: Number(form.dayOfMonth),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['recurring'] });
      setAdding(false);
      setForm({ categoryId: '', amount: '', dayOfMonth: '1' });
    },
  });

  const toggle = useMutation({
    mutationFn: (rule: RecurringExpense) =>
      api.patch(`/recurring/${rule.id}`, { active: !rule.active }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['recurring'] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/recurring/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['recurring'] }),
  });

  return (
    <section>
      <SectionTitle
        action={
          <button
            type="button"
            onClick={() => setAdding((v) => !v)}
            className="text-xs text-accent"
          >
            {adding ? 'Annulla' : 'Aggiungi'}
          </button>
        }
      >
        Spese fisse
      </SectionTitle>

      <Card className="flex flex-col gap-3">
        <p className="text-xs text-muted">
          Vengono proposte nella Home il giorno indicato. Nessun movimento viene creato senza la tua
          conferma.
        </p>

        {adding && (
          <div className="flex flex-wrap gap-2">
            <select
              value={form.categoryId}
              onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value }))}
              aria-label="Categoria"
              className="flex-1 rounded-lg bg-surface-2 px-2 py-2 text-sm"
            >
              <option value="">Categoria</option>
              {categories
                .filter((c) => !c.archived)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </select>
            <input
              inputMode="decimal"
              value={form.amount}
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
              placeholder="Importo"
              aria-label="Importo"
              className="tnum w-24 rounded-lg bg-surface-2 px-2 py-2 text-right text-sm"
            />
            <input
              type="number"
              min={1}
              max={31}
              value={form.dayOfMonth}
              onChange={(e) => setForm((f) => ({ ...f, dayOfMonth: e.target.value }))}
              aria-label="Giorno del mese"
              className="tnum w-16 rounded-lg bg-surface-2 px-2 py-2 text-right text-sm"
            />
            <Button
              onClick={() => create.mutate()}
              disabled={!form.categoryId || !form.amount || create.isPending}
            >
              Aggiungi
            </Button>
          </div>
        )}

        {rules && rules.length > 0 ? (
          // Due righe invece di una: su 390px il nome della categoria accanto
          // a importo, giorno e due pulsanti finisce schiacciato a zero.
          rules.map((rule) => (
            <div key={rule.id} className="rounded-[var(--radius-control)] bg-surface-2 px-3 py-2">
              <div className="flex items-center gap-2">
                <Dot color={rule.category.color} />
                <span
                  className={`min-w-0 flex-1 truncate text-sm ${
                    rule.active ? '' : 'text-muted line-through'
                  }`}
                >
                  {rule.category.name}
                </span>
                <span className="tnum text-sm font-medium">{formatEuro(rule.amount)}</span>
              </div>
              <div className="mt-1 flex items-center gap-1 text-xs text-muted">
                <span>ogni {rule.dayOfMonth} del mese</span>
                <button
                  type="button"
                  onClick={() => toggle.mutate(rule)}
                  className="ml-auto rounded-lg px-2 py-1 hover:bg-surface"
                >
                  {rule.active ? 'Sospendi' : 'Attiva'}
                </button>
                <button
                  type="button"
                  onClick={() => remove.mutate(rule.id)}
                  aria-label={`Elimina ${rule.category.name}`}
                  className="rounded-lg px-2 py-1 text-danger hover:bg-danger-soft"
                >
                  Elimina
                </button>
              </div>
            </div>
          ))
        ) : (
          <p className="text-sm text-muted">Nessuna spesa fissa.</p>
        )}
      </Card>
    </section>
  );
}
